/**
 * Local (IndexedDB + download) and cloud (/api) persistence for Game Editor assets.
 */

export type AssetKind = 'model' | 'animation' | 'project';

export interface StoredAssetMeta {
  id: string;
  name: string;
  kind: AssetKind;
  mime: string;
  ext: string;
  size: number;
  notes?: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  sourceModel?: string | null;
  clips?: string[];
  /** local | cloud */
  location: 'local' | 'cloud';
  filename?: string;
}

export interface ActiveGameConfig {
  preferredAssetId: string | null;
  preferredModelUrl: string | null;
  assetName?: string | null;
  updatedAt: string | null;
  note?: string;
}

const DB_NAME = 'fencing-editor';
const DB_STORE = 'assets';
const DB_VER = 1;

function uuid(): string {
  return crypto.randomUUID?.() ?? `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(DB_STORE)) {
        db.createObjectStore(DB_STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(record: { id: string; meta: StoredAssetMeta; data: ArrayBuffer }): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function idbGet(id: string): Promise<{ id: string; meta: StoredAssetMeta; data: ArrayBuffer } | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readonly');
    const req = tx.objectStore(DB_STORE).get(id);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

async function idbAll(): Promise<{ id: string; meta: StoredAssetMeta; data: ArrayBuffer }[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readonly');
    const req = tx.objectStore(DB_STORE).getAll();
    req.onsuccess = () => resolve(req.result ?? []);
    req.onerror = () => reject(req.error);
  });
}

async function idbDelete(id: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DB_STORE, 'readwrite');
    tx.objectStore(DB_STORE).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function apiHealth(): Promise<{ ok: boolean; publishEnabled?: boolean } | null> {
  try {
    const r = await fetch('/api/health', { cache: 'no-store' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

export async function listCloudAssets(): Promise<StoredAssetMeta[]> {
  const r = await fetch('/api/assets', { cache: 'no-store' });
  if (!r.ok) throw new Error(`cloud list ${r.status}`);
  const j = await r.json();
  return (j.assets || []).map((a: StoredAssetMeta) => ({ ...a, location: 'cloud' as const }));
}

export async function listLocalAssets(): Promise<StoredAssetMeta[]> {
  const rows = await idbAll();
  return rows.map((r) => ({ ...r.meta, location: 'local' as const }));
}

export async function listAllAssets(): Promise<StoredAssetMeta[]> {
  const local = await listLocalAssets();
  let cloud: StoredAssetMeta[] = [];
  try {
    cloud = await listCloudAssets();
  } catch {
    /* offline / no api */
  }
  return [...cloud, ...local].sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
}

export async function saveLocalAsset(opts: {
  name: string;
  kind: AssetKind;
  mime: string;
  ext: string;
  data: ArrayBuffer | Blob;
  notes?: string;
  clips?: string[];
  sourceModel?: string;
  id?: string;
}): Promise<StoredAssetMeta> {
  const buf = opts.data instanceof Blob ? await opts.data.arrayBuffer() : opts.data;
  const id = opts.id || uuid();
  const now = new Date().toISOString();
  const meta: StoredAssetMeta = {
    id,
    name: opts.name,
    kind: opts.kind,
    mime: opts.mime,
    ext: opts.ext.replace(/^\./, ''),
    size: buf.byteLength,
    notes: opts.notes,
    clips: opts.clips,
    sourceModel: opts.sourceModel,
    createdAt: now,
    updatedAt: now,
    location: 'local',
  };
  await idbPut({ id, meta, data: buf });
  return meta;
}

export async function saveCloudAsset(opts: {
  name: string;
  kind: AssetKind;
  mime: string;
  ext: string;
  data: ArrayBuffer | Blob;
  notes?: string;
  clips?: string[];
  sourceModel?: string;
  id?: string;
}): Promise<StoredAssetMeta> {
  const buf = opts.data instanceof Blob ? await opts.data.arrayBuffer() : opts.data;
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  const dataBase64 = btoa(binary);
  const r = await fetch('/api/assets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: opts.id,
      name: opts.name,
      kind: opts.kind,
      mime: opts.mime,
      ext: opts.ext.replace(/^\./, ''),
      notes: opts.notes,
      clips: opts.clips,
      sourceModel: opts.sourceModel,
      dataBase64,
    }),
  });
  if (!r.ok) throw new Error(`cloud save ${r.status}: ${await r.text()}`);
  const j = await r.json();
  return { ...j.asset, location: 'cloud' };
}

export async function loadAssetData(meta: StoredAssetMeta): Promise<ArrayBuffer> {
  if (meta.location === 'local') {
    const row = await idbGet(meta.id);
    if (!row) throw new Error('Local asset missing');
    return row.data;
  }
  const r = await fetch(`/api/assets/${meta.id}/file`, { cache: 'no-store' });
  if (!r.ok) throw new Error(`cloud load ${r.status}`);
  return await r.arrayBuffer();
}

export async function deleteAsset(meta: StoredAssetMeta): Promise<void> {
  if (meta.location === 'local') {
    await idbDelete(meta.id);
    return;
  }
  const r = await fetch(`/api/assets/${meta.id}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`cloud delete ${r.status}`);
}

export async function getActiveGameConfig(): Promise<ActiveGameConfig | null> {
  try {
    const r = await fetch('/api/game/active', { cache: 'no-store' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

/** Static fallback written by publish into the repo */
export async function getStaticEditorActive(): Promise<{ preferredUrl?: string } | null> {
  try {
    const r = await fetch('/models/editor-active.json', { cache: 'no-store' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

export async function setActiveGameAsset(assetId: string | null): Promise<ActiveGameConfig> {
  const r = await fetch('/api/game/active', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(assetId ? { preferredAssetId: assetId } : { clear: true }),
  });
  if (!r.ok) throw new Error(`set active ${r.status}`);
  return await r.json();
}

export async function publishToServer(opts: {
  assetId?: string;
  replacePrimary?: boolean;
  message?: string;
  git?: boolean;
  deploy?: boolean;
}): Promise<unknown> {
  const r = await fetch('/api/publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(opts),
  });
  if (!r.ok) throw new Error(`publish ${r.status}: ${await r.text()}`);
  return await r.json();
}

export function downloadBlob(blob: Blob, filename: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export async function resolvePreferredModelUrl(fallback: string): Promise<string> {
  const active = await getActiveGameConfig();
  if (active?.preferredModelUrl) return active.preferredModelUrl;
  const st = await getStaticEditorActive();
  if (st?.preferredUrl) return st.preferredUrl;
  return fallback;
}
